import {
    ArrowLeft,
    ArrowRight,
    Check,
    Download,
    Pencil,
    Plus,
    RotateCcw,
    Save,
    Send,
    StopCircle,
    Trash2,
    Undo,
    X,
} from "lucide-react";
import type { CSSProperties } from "react";

export type ActionButtonIconType =
    | "submit"
    | "next"
    | "save"
    | "send"
    | "skip"
    | "back"
    | "cancel"
    | "delete"
    | "continue"
    | "confirm"
    | "add"
    | "edit"
    | "done"
    | "stop"
    | "retry"
    | "download";

interface ActionButtonIconProps {
    type: ActionButtonIconType;
    isHovered: boolean;
    size?: number;
}

const ICON_STYLE: CSSProperties = {
    flexShrink: 0,
    transition: "transform 300ms ease-in-out",
};

const SKIP_FLIP_STYLE: CSSProperties = {
    display: "inline-flex",
    flexShrink: 0,
    transform: "scaleX(-1)",
};

function getTransform(type: ActionButtonIconType, isHovered: boolean): string | undefined {
    switch (type) {
        case "submit":
        case "confirm":
        case "done":
            return isHovered ? "rotate(-6deg)" : "none";
        case "next":
        case "continue":
        case "edit":
            return isHovered ? "translateX(2px)" : "none";
        case "send":
            return isHovered ? "translate(1px, -1px)" : "none";
        case "save":
        case "cancel":
        case "add":
        case "delete":
        case "stop":
            return isHovered ? "rotate(10deg)" : "none";
        case "skip":
            return isHovered ? "rotate(-15deg)" : "none";
        case "back":
            return isHovered ? "translateX(-2px)" : "none";
        case "retry":
            return isHovered ? "rotate(-15deg)" : "none";
        case "download":
            return isHovered ? "translateY(2px)" : "none";
    }
}

const ActionButtonIcon = ({ type, isHovered, size = 20 }: ActionButtonIconProps) => {
    const transform = getTransform(type, isHovered);

    const style: CSSProperties = {
        ...ICON_STYLE,
        transform,
    };

    switch (type) {
        case "submit":
        case "confirm":
        case "done":
            return <Check size={size} strokeWidth={2.5} style={style} />;
        case "next":
        case "continue":
            return <ArrowRight size={size} strokeWidth={2.5} style={style} />;
        case "send":
            return <Send size={size} strokeWidth={2.5} style={style} />;
        case "save":
            return <Save size={size} strokeWidth={2.5} style={style} />;
        case "back":
            return <ArrowLeft size={size} strokeWidth={2.5} style={style} />;
        case "skip":
            return (
                <span style={SKIP_FLIP_STYLE}>
                    <Undo size={size} strokeWidth={2.5} style={style} />
                </span>
            );
        case "cancel":
            return <X size={size} strokeWidth={2.5} style={style} />;
        case "delete":
            return <Trash2 size={size} strokeWidth={2.5} style={style} />;
        case "add":
            return <Plus size={size} strokeWidth={2.5} style={style} />;
        case "edit":
            return <Pencil size={size} strokeWidth={2.5} style={style} />;
        case "stop":
            return <StopCircle size={size} strokeWidth={2.5} style={style} />;
        case "retry":
            return <RotateCcw size={size} strokeWidth={2.5} style={style} />;
        case "download":
            return <Download size={size} strokeWidth={2.5} style={style} />;
    }
};

export default ActionButtonIcon;
